import React, { useState } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import useCopyWithFeedback from "../../hooks/useCopyWithFeedback";

const VideoTranslationApiDocumentation = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || "";
  const endpoint = `${API_BASE_URL.replace(/\/?$/, "/")}api/v1/connector/video_translation_api/jobs`;

  const [activeTab, setActiveTab] = useState("flow");
  const { copied, handleCopy } = useCopyWithFeedback();

  const step1Response = `{
  "job_id": "550e8400-e29b-41d4-a716-446655440000",
  "upload_url": "https://s3.amazonaws.com/...",
  "expires_in": 7200,
  "source_language": "en-GB",
  "target_language": "es-ES",
  "include_subtitles": false,
  "include_srt_export": true,
  "include_source_srt_export": false
}`;

  const step2Response = `{
  "job_id": "550e8400-e29b-41d4-a716-446655440000",
  "status": "submitted"
}`;

  const step3Response = `{
  "job_id": "550e8400-e29b-41d4-a716-446655440000",
  "status": "completed",
  "source_language": "en-GB",
  "target_language": "es-ES",
  "download_url": "https://s3.amazonaws.com/...",
  "download_url_expires_in": 7200,
  "srt_download_url": "https://s3.amazonaws.com/...",
  "source_srt_download_url": "https://s3.amazonaws.com/..."
}`;

  const curlExample = `# Step 1: Create job and get upload URL
curl --location '${endpoint}' \\
  --header 'X-API-Key: YOUR_API_KEY' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "filename": "clip.mp4",
    "source_language": "en-GB",
    "target_language": "es-ES",
    "include_subtitles": false,
    "include_srt_export": true,
    "include_source_srt_export": false
  }'

# Step 2: Upload file to S3
curl --location 'UPLOAD_URL_FROM_STEP_1' \\
  --request PUT \\
  --header 'Content-Type: video/mp4' \\
  --data-binary '@/path/to/clip.mp4'

# Step 3: Start translation
curl --location '${endpoint}/JOB_ID/start' \\
  --request POST \\
  --header 'X-API-Key: YOUR_API_KEY'

# Step 4: Poll for result
curl --location '${endpoint}/JOB_ID' \\
  --header 'X-API-Key: YOUR_API_KEY'`;

  return (
    <Container>
      <HeaderRow>
        <Title data-emotii-exclude>Video Translation API</Title>
        <HeaderMeta>
          <Badge tone="brand" data-emotii-exclude>v1</Badge>
          <DividerDot />
          <Small>Async • S3 Upload</Small>
        </HeaderMeta>
      </HeaderRow>

      <PageContent>
        <Grid>
          <Card $span2>
            <CardHeader>
              <CardTitle>Overview</CardTitle>
            </CardHeader>
            <Note>
              This API provides <b>video-to-video translation</b>: upload a video file in one language,
              and receive a video back with dubbed audio in another language. The pipeline extracts and
              transcribes the original audio, translates it, generates dubbed speech, and re-assembles
              the video — with optional burned-in subtitles or a downloadable SRT file. The workflow is
              asynchronous to handle long processing times on longer videos.
            </Note>
            <Note style={{ marginTop: "12px" }}>
              <b>Supported formats:</b> <Mono data-emotii-exclude>.mp4, .mov, .mkv, .webm, .avi</Mono>
            </Note>
            <Note style={{ marginTop: "12px" }}>
              <b>Transcription-only mode:</b> set <Mono data-emotii-exclude>source_language</Mono> and{" "}
              <Mono data-emotii-exclude>target_language</Mono> to the <b>same value</b> to run transcription
              only — no translation and no voice cloning are performed. The output video keeps its original
              audio untouched; the transcript is delivered the same way a translation would be, via{" "}
              <Mono data-emotii-exclude>include_subtitles</Mono> (burned-in captions of the original speech)
              and/or <Mono data-emotii-exclude>include_srt_export</Mono> (downloadable <Mono data-emotii-exclude>.srt</Mono>{" "}
              via <Mono data-emotii-exclude>srt_download_url</Mono>). Set at least one of those two flags —
              otherwise there's nothing to receive back from a same-language job.
            </Note>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Workflow Steps</CardTitle>
            </CardHeader>
            <WorkflowSteps>
              <Step>
                <StepNumber>1</StepNumber>
                <StepContent>
                  <StepTitle>Create Job</StepTitle>
                  <StepDesc>
                    <Mono data-emotii-exclude>POST /jobs</Mono> - Returns job ID and S3 upload URL
                  </StepDesc>
                </StepContent>
              </Step>
              <Step>
                <StepNumber>2</StepNumber>
                <StepContent>
                  <StepTitle>Upload File</StepTitle>
                  <StepDesc>
                    <Mono data-emotii-exclude>PUT</Mono> to the S3 URL - Upload your video file directly to S3
                  </StepDesc>
                </StepContent>
              </Step>
              <Step>
                <StepNumber>3</StepNumber>
                <StepContent>
                  <StepTitle>Start Processing</StepTitle>
                  <StepDesc>
                    <Mono data-emotii-exclude>POST /jobs/:job_id/start</Mono> - Trigger video translation
                  </StepDesc>
                </StepContent>
              </Step>
              <Step>
                <StepNumber>4</StepNumber>
                <StepContent>
                  <StepTitle>Poll Result</StepTitle>
                  <StepDesc>
                    <Mono data-emotii-exclude>GET /jobs/:job_id</Mono> - Check status and get download URL
                  </StepDesc>
                </StepContent>
              </Step>
            </WorkflowSteps>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Step 1: Create Job</CardTitle>
            </CardHeader>
            <EndpointRow>
              <EndpointLabel>POST</EndpointLabel>
              <EndpointValue data-emotii-exclude>{endpoint}</EndpointValue>
            </EndpointRow>
            <Note style={{ marginTop: "12px" }}>
              <b>Headers:</b> <Mono data-emotii-exclude>X-API-Key</Mono>, <Mono data-emotii-exclude>Content-Type: application/json</Mono>
            </Note>
            <List>
              <Item>
                <Key data-emotii-exclude>filename</Key>
                <Desc>Original filename, used to validate the file extension (e.g., <Mono data-emotii-exclude>"clip.mp4"</Mono>)</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>source_language</Key>
                <Desc>Source language locale code (e.g., <Mono data-emotii-exclude>"en-GB", "fr-FR", "de-DE"</Mono>)</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>target_language</Key>
                <Desc>
                  Target language locale code (e.g., <Mono data-emotii-exclude>"es-ES", "ja-JP", "ro-RO"</Mono>).
                  Set equal to <Mono data-emotii-exclude>source_language</Mono> to run{" "}
                  <b>transcription-only mode</b> (see Overview).
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>include_subtitles</Key>
                <Desc><b>optional</b> — burn subtitles into the output video: translated subtitles for a cross-language job, or captions of the original speech for a same-language (transcription-only) job. Defaults to <Mono data-emotii-exclude>false</Mono>.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>include_srt_export</Key>
                <Desc>
                  <b>optional</b> — also generate a downloadable <Mono data-emotii-exclude>.srt</Mono> of the
                  translated-language transcript (<Mono data-emotii-exclude>srt_download_url</Mono>). Defaults
                  to <Mono data-emotii-exclude>false</Mono>.
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>include_source_srt_export</Key>
                <Desc>
                  <b>optional</b> — also generate a downloadable <Mono data-emotii-exclude>.srt</Mono> of the
                  <b> original-language</b> transcript of the same speech (<Mono data-emotii-exclude>source_srt_download_url</Mono>).
                  Independent of <Mono data-emotii-exclude>include_srt_export</Mono> — request either, both, or
                  neither. Defaults to <Mono data-emotii-exclude>false</Mono>.
                </Desc>
              </Item>
            </List>
            <Note style={{ marginTop: "12px" }}>
              <b>Response:</b>
            </Note>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{step1Response}</code>
              </CodeArea>
            </CodeBlock>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Step 2: Upload to S3</CardTitle>
            </CardHeader>
            <Note>
              Upload your video file to the <Mono data-emotii-exclude>upload_url</Mono> from Step 1.
              URL expires in 2 hours.
            </Note>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{`curl --request PUT \\
  --header 'Content-Type: video/mp4' \\
  --data-binary '@/path/to/clip.mp4' \\
  'UPLOAD_URL_FROM_STEP_1'`}</code>
              </CodeArea>
            </CodeBlock>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Step 3: Start Translation</CardTitle>
            </CardHeader>
            <EndpointRow>
              <EndpointLabel>POST</EndpointLabel>
              <EndpointValue data-emotii-exclude>{endpoint}/:job_id/start</EndpointValue>
            </EndpointRow>
            <Note style={{ marginTop: "12px" }}>
              <b>Headers:</b> <Mono data-emotii-exclude>X-API-Key</Mono>
            </Note>
            <Note>
              This triggers the full pipeline: audio extraction → transcription → translation → dubbed
              speech generation → video re-assembly. In transcription-only mode (<Mono data-emotii-exclude>source_language</Mono>{" "}
              == <Mono data-emotii-exclude>target_language</Mono>), translation and dubbed speech generation are skipped.
            </Note>
            <Note>
              <b>Response:</b>
            </Note>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{step2Response}</code>
              </CodeArea>
            </CodeBlock>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Step 4: Poll for Result</CardTitle>
            </CardHeader>
            <EndpointRow>
              <EndpointLabel>GET</EndpointLabel>
              <EndpointValue data-emotii-exclude>{endpoint}/:job_id</EndpointValue>
            </EndpointRow>
            <Note style={{ marginTop: "12px" }}>
              <b>Headers:</b> <Mono data-emotii-exclude>X-API-Key</Mono>
            </Note>
            <Note>
              Poll this endpoint every few seconds. Possible statuses: <Mono data-emotii-exclude>pending</Mono>,{" "}
              <Mono data-emotii-exclude>uploading</Mono>, <Mono data-emotii-exclude>submitted</Mono>,{" "}
              <Mono data-emotii-exclude>processing</Mono>, <Mono data-emotii-exclude>completed</Mono>,{" "}
              <Mono data-emotii-exclude>failed</Mono>, <Mono data-emotii-exclude>rejected</Mono>
            </Note>
            <Note>
              When status is <Mono data-emotii-exclude>completed</Mono>, use <Mono data-emotii-exclude>download_url</Mono> to
              download the translated video. If <Mono data-emotii-exclude>include_srt_export</Mono> was requested,{" "}
              <Mono data-emotii-exclude>srt_download_url</Mono> (translated-language transcript) is also included.
              If <Mono data-emotii-exclude>include_source_srt_export</Mono> was requested,{" "}
              <Mono data-emotii-exclude>source_srt_download_url</Mono> (original-language transcript of the same
              speech) is included too — the two are independent, so either, both, or neither may be present.
              All URLs expire after 2 hours.
              When status is <Mono data-emotii-exclude>failed</Mono>, the response includes an{" "}
              <Mono data-emotii-exclude>error_message</Mono> field.
            </Note>
            <Note>
              <b>Response (completed):</b>
            </Note>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{step3Response}</code>
              </CodeArea>
            </CodeBlock>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Complete Example</CardTitle>
              <Tabs>
                <Tab
                  aria-selected={activeTab === "flow"}
                  onClick={() => setActiveTab("flow")}
                >
                  Flow Diagram
                </Tab>
                <Tab
                  aria-selected={activeTab === "curl"}
                  onClick={() => setActiveTab("curl")}
                >
                  cURL
                </Tab>
              </Tabs>
            </CardHeader>

            {activeTab === "flow" && (
              <FlowDiagram>
                <FlowStep>
                  <FlowLabel>Client</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>POST /jobs</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>Get job_id + upload_url</FlowLabel>
                </FlowStep>
                <FlowStep>
                  <FlowLabel>Client</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>PUT to S3 URL</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>Upload complete</FlowLabel>
                </FlowStep>
                <FlowStep>
                  <FlowLabel>Client</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>POST /:job_id/start</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>Processing started</FlowLabel>
                </FlowStep>
                <FlowStep>
                  <FlowLabel>Server</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>Transcribe + Translate + Dub + Re-assemble</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>Upload to S3</FlowLabel>
                </FlowStep>
                <FlowStep>
                  <FlowLabel>Client</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>GET /:job_id (poll)</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>Get download URL</FlowLabel>
                </FlowStep>
              </FlowDiagram>
            )}

            {activeTab === "curl" && (
              <CodeBlock>
                <Toolbar>
                  <LangPill data-emotii-exclude>bash</LangPill>
                  <Spacer />
                  <CopyBtn
                    onClick={() => handleCopy("curl", curlExample)}
                    data-copied={copied === "curl" ? "true" : "false"}
                  >
                    <CopyIcon />
                    {copied === "curl" ? "Copied" : "Copy"}
                  </CopyBtn>
                </Toolbar>
                <CodeArea role="region" aria-label="curl Example" data-emotii-exclude>
                  <code>{curlExample}</code>
                </CodeArea>
              </CodeBlock>
            )}
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Error Handling</CardTitle>
            </CardHeader>
            <ErrorList>
              <ErrorRow>
                <ErrCode data-emotii-exclude>401</ErrCode>
                <ErrText>Unauthorized — missing or invalid <Mono data-emotii-exclude>X-API-Key</Mono>.</ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode data-emotii-exclude>402</ErrCode>
                <ErrText>Payment Required — no active subscription or usage limit exceeded.</ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode data-emotii-exclude>403</ErrCode>
                <ErrText>Forbidden — API key not configured for, or not authorized for, the Video Translation API.</ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode data-emotii-exclude>404</ErrCode>
                <ErrText>Not Found — job_id doesn't exist or doesn't belong to your project.</ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode data-emotii-exclude>422</ErrCode>
                <ErrText>Unprocessable Entity — unsupported video format, job already started, or file not yet uploaded to S3.</ErrText>
              </ErrorRow>
            </ErrorList>
          </Card>
        </Grid>
      </PageContent>
    </Container>
  );
};

export default VideoTranslationApiDocumentation;

/* ---------------- Styles ---------------- */

const Container = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
  width: 100%;
  background: #fafafa;
  overflow: hidden;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  padding: 16px 24px;
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Title = styled.h2`
  color: ${lightThemeColors.darkColor};
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
  margin: 0;
`;

const HeaderMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const Small = styled.span`
  font-size: 12px;
  color: rgba(56, 56, 58, 0.7);
`;

const DividerDot = styled.span`
  width: 4px;
  height: 4px;
  background: rgba(56, 56, 58, 0.35);
  border-radius: 50%;
  display: inline-block;
`;

const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${(props) =>
    props.tone === "brand" ? lightThemeColors.primaryColor : "rgba(56,56,58,0.1)"};
  color: ${(props) => (props.tone === "brand" ? "#fff" : lightThemeColors.darkColor)};
`;

const PageContent = styled.div`
  overflow: auto;
  padding: 24px;
`;

const Grid = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(12, 1fr);
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  border-radius: 14px;
  padding: 18px;
  grid-column: ${(props) => (props.$span2 ? "span 12" : "span 6")};

  @media (max-width: 1024px) {
    grid-column: 1 / -1;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const CardTitle = styled.h3`
  color: ${lightThemeColors.darkColor};
  font-size: 16px;
  font-weight: 800;
  margin: 0;
`;

const Note = styled.p`
  color: rgba(56, 56, 58, 0.75);
  font-size: 13px;
  line-height: 1.6;
  margin: 4px 0 14px;

  a {
    color: ${lightThemeColors.primaryColor};
  }
`;

const List = styled.div`
  display: grid;
  gap: 10px;
`;

const Item = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 12px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Key = styled.div`
  color: ${lightThemeColors.darkColor};
  font-weight: 700;
  font-size: 13px;
  background: #f5f5f5;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  width: fit-content;
`;

const Desc = styled.div`
  color: rgba(56, 56, 58, 0.9);
  font-size: 13px;
  line-height: 1.6;
`;

const Tabs = styled.div`
  display: inline-flex;
  background: rgba(56, 56, 58, 0.06);
  border-radius: 10px;
  padding: 4px;
  gap: 4px;
`;

const Tab = styled.button`
  border: none;
  background: transparent;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 800;
  color: ${lightThemeColors.darkColor};
  cursor: pointer;
  transition: all 0.15s ease;

  &[aria-selected="true"] {
    background: #fff;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
  }
`;

const CodeBlock = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(34, 39, 46, 1);
  color: #fff;
  border-radius: 12px;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
`;

const LangPill = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
  background: rgba(72, 165, 240, 0.18);
  color: rgba(72, 165, 240, 1);
  border: 1px solid rgba(72, 165, 240, 0.35);
  padding: 4px 8px;
  border-radius: 999px;
`;

const Spacer = styled.div`
  flex: 1;
`;

const CopyBtn = styled.button`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &[data-copied="true"] {
    background: rgba(72, 165, 240, 0.2);
    border-color: rgba(72, 165, 240, 0.5);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const CodeArea = styled.pre`
  font-family: "Menlo", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-size: 12.5px;
  line-height: 1.45;
  padding: 14px;
  margin: 0;
  max-width: 100%;
  overflow: auto;
  white-space: pre;
  word-break: normal;

  code {
    font-family: inherit;
  }
`;

const ErrorList = styled.div`
  display: grid;
  gap: 10px;
`;

const ErrorRow = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr;
  gap: 12px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const ErrCode = styled.div`
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.3px;
  border-radius: 6px;
  padding: 6px 10px;
  text-align: center;
  width: fit-content;
  color: #991b1b;
  background: #fee2e2;
  border: 1px solid #fecaca;
`;

const ErrText = styled.div`
  color: rgba(56, 56, 58, 0.9);
  font-size: 13px;
`;

const Mono = styled.code`
  font-family: "Menlo", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-size: 0.95em;
`;

const EndpointRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const EndpointLabel = styled.div`
  color: #059669;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  background: #d1fae5;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #a7f3d0;
`;

const EndpointValue = styled.div`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 600;
  word-break: break-all;
  font-family: "Menlo", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
`;

const WorkflowSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Step = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const StepNumber = styled.div`
  background: ${lightThemeColors.primaryColor};
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 14px;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: ${lightThemeColors.darkColor};
  margin-bottom: 4px;
`;

const StepDesc = styled.div`
  font-size: 13px;
  color: rgba(56, 56, 58, 0.75);
  line-height: 1.5;
`;

const FlowDiagram = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
`;

const FlowStep = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FlowLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${lightThemeColors.darkColor};
  background: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.08);
`;

const FlowArrow = styled.div`
  font-size: 18px;
  color: ${lightThemeColors.primaryColor};
  font-weight: 700;
`;

const CopyIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3.33333 10H2.66667C2.31304 10 1.97391 9.85952 1.72386 9.60947C1.47381 9.35943 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31304 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31304 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35943 1.47381 9.60947 1.72386C9.85952 1.97391 10 2.31304 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
